import { Catalog } from '@cdn/contracts';
import type { Locale } from './locale';
import type { Missing } from './missing';
import en from './catalogs/en.json';
import es from './catalogs/es.json';

export type Values = Record<string, string | number>;

/**
 * Text of the interface in the selected language. `text()` reads the catalogs of this application; `shared`
 * reads the contracts catalog that names statuses, events, errors and help. Raw codes and identifiers are
 * never looked up or translated: screens show them beside the localized explanation.
 */
export class Translator {
	static readonly tables: Record<string, Record<string, string>> = { en, es };

	#locale: Locale;
	#missing: Missing;

	constructor(locale: Locale, missing: Missing) {
		this.#locale = locale;
		this.#missing = missing;
	}

	/**
	 * The contracts catalog in the selected language
	 */
	get shared() {
		return new Catalog(this.#locale.code);
	}

	/**
	 * @returns The text of the key with `{name}` placeholders replaced. A key no catalog holds is reported and
	 * answered with a neutral ellipsis, so a raw key never reaches the screen.
	 */
	text = (key: string, values: Values = {}): string => {
		for (const code of this.#locale.chain) {
			const found = Translator.tables[code]?.[key];
			if (found === undefined) continue;
			code !== this.#locale.code && this.#missing.add(key, this.#locale.code, 'fallback');
			return this.#complete(key, found, values);
		}
		this.#missing.add(key, this.#locale.code, 'absent');
		return '…';
	};

	/** Whether the chain of the selected language holds the key */
	#held = (key: string): boolean => this.#locale.chain.some(code => key in (Translator.tables[code] ?? {}));

	/**
	 * Fills the `{name}` placeholders of a message and guarantees that none of them reaches the screen.
	 *
	 * A message whose values are missing — an event that arrives without the members its summary names — is
	 * answered with its reduced form, the sentence of this catalog that reads correctly without them
	 * (`events.plain.<type>` for a shared `event.<type>` summary), and never with an invented number. When
	 * no reduced form exists, a neutral sentence says that the values did not arrive.
	 */
	#complete = (key: string, text: string, values: Values): string => {
		const filled = text.replace(/\{([a-z]+)\}/g, (placeholder, name: string) => (name in values ? String(values[name]) : placeholder));
		if (!/\{[a-z]+\}/.test(filled)) return filled;

		this.#missing.add(key, this.#locale.code, 'incomplete');
		const reduced = key.startsWith('event.') ? `events.plain.${key.slice('event.'.length)}` : undefined;
		return reduced && this.#held(reduced) ? this.text(reduced, values) : this.text('i18n.incomplete');
	};

	/**
	 * Selects `<key>.one`, `<key>.other`… by the plural rule of the language; `{count}` is always available
	 */
	plural = (key: string, count: number, values: Values = {}): string => {
		const rule = new Intl.PluralRules(this.#locale.code).select(count);
		const exact = `${key}.${rule}`;
		const held = this.#locale.chain.some(code => exact in (Translator.tables[code] ?? {}));
		return this.text(held ? exact : `${key}.other`, { count, ...values });
	};

	/**
	 * Text of the contracts catalog: job states, event summaries, help
	 */
	term = (key: string, values: Values = {}): string => {
		const catalog = this.shared;
		if (!catalog.has(key)) {
			this.#missing.add(key, this.#locale.code, 'absent');
			return '…';
		}
		catalog.entry(key)?.fallback && this.#missing.add(key, this.#locale.code, 'fallback');
		return this.#complete(key, catalog.text(key, values), values);
	};

	/**
	 * Localized explanation of an error or failure code; generic for a code this version does not know
	 */
	error = (code: string): string => this.shared.error(code);
}
